import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, TrendingUp, TrendingDown, Star, Bell, ChevronRight, ChevronDown, Info, Phone, ArrowRight, ExternalLink } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const PHONE_NUMBER = "+17787590400";

// Comprehensive crypto content database (keep the same data)
const CRYPTO_CONTENT = {
  bitcoin: {
    symbol: "BTC",
    name: "Bitcoin",
    tagline: "The world's first decentralized cryptocurrency",
    about: `Bitcoin is the world's first decentralized cryptocurrency that enables peer-to-peer electronic cash transactions without intermediaries like banks or governments.

Bitcoin operates on a blockchain secured by proof-of-work mining and the SHA-256 cryptographic algorithm, making it virtually impossible to counterfeit or double-spend.

With a fixed supply cap of 21 million coins and programmatic halvings every four years, Bitcoin is designed as a deflationary digital asset often called "digital gold."`,
    creator: `Bitcoin was created by an individual or group using the pseudonym Satoshi Nakamoto, whose true identity remains unknown. In October 2008, Nakamoto published the Bitcoin whitepaper titled "Bitcoin: A Peer-to-Peer Electronic Cash System."

The first Bitcoin block, known as the genesis block, was mined on January 9, 2009.`,
    howItWorks: `Bitcoin operates as a decentralized peer-to-peer network where users exchange value directly without intermediaries. The network maintains a public ledger called the blockchain.

Transactions are grouped into blocks, with each block containing a cryptographic hash of the previous block. New blocks are added approximately every 10 minutes through mining.`,
    security: `Bitcoin's security is rooted in the SHA-256 cryptographic hash function, a proven algorithm also used to secure critical government and internet infrastructure.`,
    storage: `Securing Bitcoin means protecting private keys. Hardware wallets provide the highest security for long-term storage. Software wallets offer convenience but require stronger security practices.`,
    volatility: `Bitcoin's price is volatile due to fixed supply with fluctuating demand, a 24/7 speculative market, and sensitivity to macroeconomic factors.`,
    value: `Bitcoin's value stems from solving the double-spending problem without trusted intermediaries, creating the first truly scarce digital asset with a fixed supply cap of 21 million coins.`,
    maxSupply: 21000000,
    website: "https://bitcoin.org",
    whitepaper: "https://bitcoin.org/bitcoin.pdf"
  },
  ethereum: {
    symbol: "ETH",
    name: "Ethereum",
    tagline: "Decentralized platform for smart contracts and dApps",
    about: `Ethereum is a decentralized platform that enables smart contracts and decentralized applications (dApps) to be built and run without downtime, fraud, or interference.

Unlike Bitcoin, Ethereum is a programmable blockchain that serves as the foundation for thousands of applications in DeFi, NFTs, and Web3.

Ethereum transitioned from proof-of-work to proof-of-stake in 2022 (The Merge), reducing energy consumption by approximately 99.95%.`,
    creator: `Ethereum was proposed by Vitalik Buterin in late 2013 and development began in early 2014. The network went live on July 30, 2015.`,
    howItWorks: `Ethereum uses smart contracts—self-executing code stored on the blockchain. When conditions are met, the contract automatically executes without intermediaries.`,
    security: `Ethereum's proof-of-stake consensus requires validators to stake 32 ETH as collateral. Malicious behavior results in "slashing" — loss of staked ETH.`,
    storage: `ETH can be stored in hardware wallets, software wallets, or on exchanges. For DeFi and dApp interaction, browser extension wallets like MetaMask are popular.`,
    volatility: `ETH's price is influenced by network activity, gas fees, DeFi TVL, NFT market conditions, and overall crypto market sentiment.`,
    value: `Ethereum's value comes from being the leading smart contract platform with the largest developer ecosystem and application network effects.`,
    maxSupply: null,
    website: "https://ethereum.org",
    whitepaper: "https://ethereum.org/whitepaper"
  },
  solana: {
    symbol: "SOL",
    name: "Solana",
    tagline: "High-performance blockchain for decentralized apps",
    about: `Solana is a high-performance blockchain known for its speed and low transaction costs. It can process over 65,000 transactions per second with sub-second finality.

The network uses a unique Proof of History (PoH) consensus mechanism combined with Proof of Stake.`,
    creator: `Solana was founded by Anatoly Yakovenko in 2017. The mainnet beta launched in March 2020.`,
    howItWorks: `Proof of History (PoH) creates a historical record that proves events occurred at a specific moment, enabling parallel transaction processing.`,
    security: `Solana uses a combination of PoH and Tower BFT consensus for security. Validators must stake SOL to participate.`,
    storage: `Phantom and Solflare are popular Solana wallets. Hardware wallets like Ledger also support SOL.`,
    volatility: `SOL's price is influenced by network performance, ecosystem growth, and competition from other L1s.`,
    value: `Solana's value proposition is speed and low cost, ideal for high-frequency applications, gaming, and consumer apps.`,
    maxSupply: null,
    website: "https://solana.com",
    whitepaper: "https://solana.com/solana-whitepaper.pdf"
  },
  cardano: {
    symbol: "ADA",
    name: "Cardano",
    tagline: "Research-driven blockchain platform",
    about: `Cardano is a proof-of-stake blockchain built using peer-reviewed academic research and formal verification methods.

The platform is designed for sustainability, interoperability, and scalability, with a focus on developing countries.`,
    creator: `Cardano was founded by Charles Hoskinson, a co-founder of Ethereum, in 2015. Development is led by IOHK.`,
    howItWorks: `Ouroboros is Cardano's proof-of-stake protocol, the first to be mathematically proven secure through peer-reviewed research.`,
    security: `Cardano's security comes from academic rigor and formal verification. Smart contracts are written in Haskell-based Plutus.`,
    storage: `Daedalus and Yoroi are official Cardano wallets. Staking can be done directly from wallets without locking funds.`,
    volatility: `ADA's price responds to development milestones, smart contract adoption, and overall market sentiment.`,
    value: `Cardano's value comes from its scientific approach and real-world applications in identity and supply chain.`,
    maxSupply: 45000000000,
    website: "https://cardano.org",
    whitepaper: "https://docs.cardano.org/introduction"
  },
  tether: {
    symbol: "USDT",
    name: "Tether",
    tagline: "The most widely used stablecoin",
    about: `Tether (USDT) is a stablecoin pegged to the US Dollar, designed to maintain a 1:1 value with USD.

As the largest stablecoin by market cap, USDT is widely used for trading, transfers, and as a stable store of value.`,
    creator: `Tether was launched in 2014 by Tether Limited, originally as Realcoin.`,
    howItWorks: `Each USDT is supposedly backed by reserves held by Tether Limited. Users can redeem USDT for USD through the company.`,
    security: `USDT's security depends on trust in Tether Limited's reserves and the underlying blockchain.`,
    storage: `USDT can be stored in any wallet supporting the blockchain it's issued on (ERC-20, TRC-20, etc.).`,
    volatility: `As a stablecoin, USDT is designed to have minimal volatility around $1.`,
    value: `USDT's value comes from being the most liquid stablecoin, essential for trading pairs on most exchanges.`,
    maxSupply: null,
    website: "https://tether.to",
    whitepaper: "https://tether.to/whitepaper"
  },
  ripple: {
    symbol: "XRP",
    name: "XRP",
    tagline: "Digital payment protocol for fast, low-cost transfers",
    about: `XRP is the native cryptocurrency of the XRP Ledger, designed for fast, low-cost international payments.

The XRP Ledger uses a consensus protocol that doesn't require mining, enabling transactions to settle in 3-5 seconds.`,
    creator: `XRP was created by Jed McCaleb, Arthur Britto, and David Schwartz. Ripple Labs was founded in 2012.`,
    howItWorks: `The XRP Ledger uses Federated Byzantine Agreement consensus, where trusted validators agree on transactions.`,
    security: `Security comes from the consensus of trusted validators chosen by network participants.`,
    storage: `XUMM (now Xaman) is the most popular XRP wallet. A minimum reserve of 10 XRP is required.`,
    volatility: `XRP's price is heavily influenced by regulatory news, particularly related to the SEC.`,
    value: `XRP's value comes from its utility in cross-border payments and Ripple's partnerships with financial institutions.`,
    maxSupply: 100000000000,
    website: "https://ripple.com",
    whitepaper: "https://ripple.com/files/ripple_consensus_whitepaper.pdf"
  },
  binancecoin: {
    symbol: "BNB",
    name: "BNB",
    tagline: "Utility token powering the Binance ecosystem",
    about: `BNB is the native cryptocurrency of the BNB Chain and utility token for the Binance ecosystem.

BNB Chain is one of the most active smart contract platforms, known for low fees and EVM compatibility.`,
    creator: `BNB was created by Binance, founded by Changpeng Zhao (CZ) in 2017.`,
    howItWorks: `BNB Chain uses Proof of Staked Authority (PoSA), combining delegated PoS with proof of authority.`,
    security: `Security relies on a limited set of validators chosen through staking and reputation.`,
    storage: `Trust Wallet and MetaMask support BNB. Be careful to select the correct network.`,
    volatility: `BNB's price correlates with Binance's success and overall crypto market conditions.`,
    value: `BNB's value comes from utility across the Binance ecosystem—trading discounts, DeFi, launchpad participation.`,
    maxSupply: 200000000,
    website: "https://www.bnbchain.org",
    whitepaper: "https://github.com/bnb-chain/whitepaper"
  },
  dogecoin: {
    symbol: "DOGE",
    name: "Dogecoin",
    tagline: "The people's cryptocurrency",
    about: `Dogecoin started as a joke cryptocurrency in 2013 featuring the Shiba Inu dog meme but has grown into a legitimate payment method.

With no supply cap and fast block times, Dogecoin is designed for everyday transactions.`,
    creator: `Dogecoin was created by Billy Markus and Jackson Palmer in December 2013 as a parody of Bitcoin.`,
    howItWorks: `Dogecoin uses proof-of-work with Scrypt algorithm, merged-mined with Litecoin for security.`,
    security: `Merged mining with Litecoin provides security from LTC miners without additional energy use.`,
    storage: `Dogecoin Core is the official wallet. MultiDoge provides a lighter option.`,
    volatility: `DOGE is highly volatile, often driven by social media trends and celebrity tweets.`,
    value: `Dogecoin's value comes from community, brand recognition, and use as a tipping currency.`,
    maxSupply: null,
    website: "https://dogecoin.com",
    whitepaper: null
  },
  tron: {
    symbol: "TRX",
    name: "TRON",
    tagline: "Decentralizing the entertainment industry",
    about: `TRON is a blockchain platform focused on content sharing and decentralized applications.

The network handles more USDT transactions than any other blockchain, making it crucial for stablecoin transfers.`,
    creator: `TRON was founded by Justin Sun in 2017. The TRON Foundation is based in Singapore.`,
    howItWorks: `27 Super Representatives (validators) process transactions, elected by TRX holders.`,
    security: `Security relies on the reputation and stake of Super Representatives.`,
    storage: `TronLink is the most popular TRON wallet.`,
    volatility: `TRX price is influenced by network activity, USDT volume, and overall market conditions.`,
    value: `TRON's value comes from being the preferred network for USDT transfers due to low fees.`,
    maxSupply: null,
    website: "https://tron.network",
    whitepaper: "https://tron.network/static/doc/white_paper_v_2_0.pdf"
  },
  "avalanche-2": {
    symbol: "AVAX",
    name: "Avalanche",
    tagline: "The fastest smart contracts platform",
    about: `Avalanche is a layer-1 blockchain known for its speed, low costs, and eco-friendliness.

The platform can process 4,500+ transactions per second with sub-second finality.`,
    creator: `Avalanche was created by Ava Labs, founded by Cornell professor Emin Gün Sirer.`,
    howItWorks: `Avalanche uses a novel consensus protocol that achieves finality in under 1 second through repeated random sampling.`,
    security: `Security comes from a large validator set and economic incentives through staking.`,
    storage: `Core wallet is the official Avalanche wallet. MetaMask works for C-Chain.`,
    volatility: `AVAX price is influenced by DeFi activity, subnet launches, and competition with other L1s.`,
    value: `Avalanche's value comes from performance, subnet flexibility, and institutional adoption.`,
    maxSupply: 720000000,
    website: "https://avax.network",
    whitepaper: "https://www.avalabs.org/whitepapers"
  }
};

const DEFAULT_CONTENT = {
  about: "This cryptocurrency is available for trading at FonderX. Contact us for the best rates.",
  creator: "Information about the creators of this cryptocurrency.",
  howItWorks: "This blockchain operates using its own consensus mechanism.",
  security: "Security features vary by cryptocurrency.",
  storage: "Store your cryptocurrency in a secure wallet.",
  volatility: "Cryptocurrency prices can be highly volatile.",
  value: "Value is determined by supply, demand, utility, and market sentiment.",
  maxSupply: null,
  website: null,
  whitepaper: null
};

// Format functions
const formatPrice = (price) => {
  if (!price) return "$0.00";
  if (price >= 1000) return `$${price.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1) return `$${price.toFixed(2)}`;
  if (price >= 0.01) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(6)}`;
};

const formatLargeNumber = (num) => {
  if (!num) return "-";
  if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  return `$${num.toLocaleString()}`;
};

const formatSupply = (num) => {
  if (!num) return "-";
  if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  return num.toLocaleString();
};

// Responsive Price Chart
const PriceChart = ({ data, isPositive }) => {
  if (!data || data.length === 0) {
    return (
      <div className="w-full h-48 sm:h-64 bg-gray-50 rounded-xl flex items-center justify-center">
        <div className="animate-pulse text-gray-400 text-sm">Loading chart...</div>
      </div>
    );
  }
  
  const values = data.map(d => d.price);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  
  const strokeColor = isPositive ? "#3B82F6" : "#EF4444";
  
  // Create SVG path
  const createPath = (width, height) => {
    const padding = { top: 20, bottom: 30, left: 10, right: 10 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    const points = values.map((value, index) => {
      const x = padding.left + (index / (values.length - 1)) * chartWidth;
      const y = padding.top + (1 - (value - min) / range) * chartHeight;
      return { x, y };
    });
    
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    const areaPath = linePath + ` L ${points[points.length - 1].x.toFixed(1)} ${height - padding.bottom} L ${padding.left} ${height - padding.bottom} Z`;
    
    return { linePath, areaPath, points };
  };

  return (
    <div className="relative w-full">
      {/* Mobile Chart */}
      <svg viewBox="0 0 350 200" className="w-full h-auto sm:hidden" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="chartGradientMobile" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={strokeColor} stopOpacity="0.2" />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
          </linearGradient>
        </defs>
        {(() => {
          const { linePath, areaPath } = createPath(350, 200);
          return (
            <>
              <path d={areaPath} fill="url(#chartGradientMobile)" />
              <path d={linePath} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </>
          );
        })()}
        {/* Price labels */}
        <text x="340" y="30" className="text-[10px] fill-gray-400" textAnchor="end">{formatPrice(max)}</text>
        <text x="340" y="175" className="text-[10px] fill-gray-400" textAnchor="end">{formatPrice(min)}</text>
      </svg>
      
      {/* Desktop Chart */}
      <svg viewBox="0 0 600 280" className="w-full h-auto hidden sm:block" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="chartGradientDesktop" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={strokeColor} stopOpacity="0.2" />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[0.25, 0.5, 0.75].map((ratio, i) => (
          <line key={i} x1="10" y1={20 + ratio * 220} x2="590" y2={20 + ratio * 220} stroke="#E5E7EB" strokeWidth="1" />
        ))}
        {(() => {
          const { linePath, areaPath } = createPath(600, 280);
          return (
            <>
              <path d={areaPath} fill="url(#chartGradientDesktop)" />
              <path d={linePath} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </>
          );
        })()}
        {/* Price labels */}
        <text x="590" y="30" className="text-xs fill-gray-400" textAnchor="end">{formatPrice(max)}</text>
        <text x="590" y="130" className="text-xs fill-gray-400" textAnchor="end">{formatPrice((max + min) / 2)}</text>
        <text x="590" y="240" className="text-xs fill-gray-400" textAnchor="end">{formatPrice(min)}</text>
      </svg>
      
      <div className="absolute bottom-1 right-2 sm:bottom-2 sm:right-4 flex items-center gap-1 text-[10px] sm:text-xs text-gray-400">
        <span>Data by</span>
        <span className="font-medium text-gray-500">CoinGecko</span>
      </div>
    </div>
  );
};

// Expandable content section
const ContentSection = ({ title, content, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-3 sm:py-4 text-left"
      >
        <h3 className="text-sm sm:text-base font-semibold text-gray-900 pr-4">{title}</h3>
        <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="pb-4 text-gray-600 text-sm sm:text-base leading-relaxed whitespace-pre-line">
          {content}
        </div>
      )}
    </div>
  );
};

// Stats row
const StatRow = ({ label, value, tooltip }) => (
  <div className="flex items-center justify-between py-2.5 sm:py-3 border-b border-gray-100 last:border-b-0">
    <div className="flex items-center gap-1.5 sm:gap-2">
      <span className="text-gray-600 text-xs sm:text-sm">{label}</span>
      {tooltip && <Info className="w-3 h-3 text-gray-400 hidden sm:block" />}
    </div>
    <span className="font-semibold text-gray-900 text-xs sm:text-sm">{value}</span>
  </div>
);

export default function CryptoDetail() {
  const { coinId } = useParams();
  const [crypto, setCrypto] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [timeRange, setTimeRange] = useState("7");
  
  const content = CRYPTO_CONTENT[coinId] || { ...DEFAULT_CONTENT, name: coinId, symbol: coinId?.toUpperCase() };
  
  useEffect(() => {
    fetchCryptoData();
  }, [coinId]);
  
  useEffect(() => {
    fetchChartData();
  }, [coinId, timeRange]);
  
  const fetchCryptoData = async () => {
    try {
      const response = await axios.get(`${API}/api/crypto/prices`);
      const found = response.data.prices.find(c => c.id === coinId);
      if (found) setCrypto(found);
    } catch (error) {
      console.error("Error fetching crypto data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchChartData = async () => {
    setChartLoading(true);
    try {
      const response = await axios.get(`${API}/api/crypto/chart/${coinId}?days=${timeRange}`);
      setChartData(response.data.prices || []);
    } catch (error) {
      console.error("Error fetching chart data:", error);
    } finally {
      setChartLoading(false);
    }
  };
  
  const timeRanges = [
    { label: "24H", value: "1" },
    { label: "7D", value: "7" },
    { label: "1M", value: "30" },
    { label: "3M", value: "90" },
    { label: "1Y", value: "365" },
  ];
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!crypto) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center">Cryptocurrency not found</h2>
        <Link to="/crypto" className="text-blue-500 hover:underline flex items-center gap-2 text-sm sm:text-base">
          <ArrowLeft className="w-4 h-4" /> Back to Crypto
        </Link>
      </div>
    );
  }
  
  const isPositive = crypto.change_24h >= 0;
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="crypto-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-2.5 sm:py-3">
          <div className="flex items-center gap-2 text-xs sm:text-sm">
            <Link to="/crypto" className="text-gray-500 hover:text-blue-600">Crypto</Link>
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
            <span className="text-gray-900 font-medium truncate">{crypto.name}</span>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
        {/* Mobile Layout */}
        <div className="lg:hidden space-y-4">
          {/* Header Card */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <img src={crypto.image} alt={crypto.name} className="w-10 h-10 rounded-full" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-bold text-gray-900 truncate">{crypto.name}</h1>
                  <span className="text-gray-400 text-sm">{crypto.symbol}</span>
                </div>
                <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Rank #{crypto.rank}</span>
              </div>
            </div>
            
            {/* Price */}
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-2xl font-bold text-gray-900">{formatPrice(crypto.price_cad)}</span>
              <span className={`flex items-center gap-1 text-sm font-semibold ${isPositive ? 'text-[#3B82F6]' : 'text-red-500'}`}>
                {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {isPositive ? '+' : ''}{crypto.change_24h?.toFixed(2)}%
              </span>
            </div>
            
            {/* Buy/Sell Button */}
            <Link 
              to="/contact" 
              className="block w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold py-3 rounded-xl text-center transition-colors"
            >
              Buy / Sell {crypto.symbol}
            </Link>
          </div>
          
          {/* Chart Card */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            {/* Time Range Selector */}
            <div className="flex gap-1 mb-4 overflow-x-auto pb-1 -mx-1 px-1">
              {timeRanges.map(range => (
                <button
                  key={range.value}
                  onClick={() => setTimeRange(range.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
                    timeRange === range.value 
                      ? 'bg-[#001435] text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
            
            {/* Chart */}
            <div className="relative">
              {chartLoading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                  <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
              )}
              <PriceChart data={chartData} isPositive={isPositive} />
            </div>
          </div>
          
          {/* Key Stats Card */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-2 text-sm">Key Stats</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-[10px] text-gray-500 mb-0.5">Market Cap</p>
                <p className="font-bold text-gray-900 text-sm">{formatLargeNumber(crypto.market_cap)}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-[10px] text-gray-500 mb-0.5">24h Volume</p>
                <p className="font-bold text-gray-900 text-sm">{formatLargeNumber(crypto.volume_24h)}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-[10px] text-gray-500 mb-0.5">7d Change</p>
                <p className={`font-bold text-sm ${crypto.change_7d >= 0 ? 'text-[#3B82F6]' : 'text-red-500'}`}>
                  {crypto.change_7d >= 0 ? '+' : ''}{crypto.change_7d?.toFixed(2)}%
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-[10px] text-gray-500 mb-0.5">Rank</p>
                <p className="font-bold text-gray-900 text-sm">#{crypto.rank}</p>
              </div>
            </div>
          </div>
          
          {/* About Section */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="font-bold text-gray-900 mb-3">About {crypto.name}</h2>
            <div className="space-y-0">
              <ContentSection title={`What is ${crypto.name}?`} content={content.about} defaultOpen={true} />
              <ContentSection title="How It Works" content={content.howItWorks} />
              <ContentSection title="Security" content={content.security} />
              <ContentSection title="Storage" content={content.storage} />
            </div>
          </div>
          
          {/* Links */}
          {content.website && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">Official Links</h3>
              <div className="flex flex-wrap gap-2">
                <a href={content.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-blue-600 text-sm bg-blue-50 px-3 py-1.5 rounded-lg">
                  <ExternalLink className="w-3.5 h-3.5" /> Website
                </a>
                {content.whitepaper && (
                  <a href={content.whitepaper} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-blue-600 text-sm bg-blue-50 px-3 py-1.5 rounded-lg">
                    <ExternalLink className="w-3.5 h-3.5" /> Whitepaper
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Coin Header Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <img src={crypto.image} alt={crypto.name} className="w-12 h-12 rounded-full" />
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-900">{crypto.name}</h1>
                    <span className="text-gray-400">{crypto.symbol}</span>
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Rank #{crypto.rank}</span>
                </div>
              </div>
              
              {/* Price */}
              <div className="mb-4">
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-3xl font-bold text-gray-900">{formatPrice(crypto.price_cad)}</span>
                  <span className={`flex items-center gap-1 text-lg font-semibold ${isPositive ? 'text-[#3B82F6]' : 'text-red-500'}`}>
                    {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    {isPositive ? '+' : ''}{crypto.change_24h?.toFixed(2)}%
                  </span>
                </div>
                <p className="text-xs text-gray-500">CAD • 24h change</p>
              </div>
              
              {/* Stats */}
              <div className="mb-4">
                <StatRow label="Market Cap" value={formatLargeNumber(crypto.market_cap)} />
                <StatRow label="24h Volume" value={formatLargeNumber(crypto.volume_24h)} />
                <StatRow label="Circulating Supply" value={formatSupply(crypto.circulating_supply)} />
                <StatRow label="Max Supply" value={content.maxSupply ? formatSupply(content.maxSupply) : formatSupply(crypto.max_supply)} />
                {crypto.ath > 0 && <StatRow label="All-Time High" value={formatPrice(crypto.ath)} />}
              </div>
              
              {/* Buy/Sell Buttons */}
              <div className="flex gap-2">
                <Link to="/contact" className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold py-3 rounded-xl text-center transition-colors">
                  Buy / Sell
                </Link>
                <a href={`tel:${PHONE_NUMBER}`} className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
                  <Phone className="w-5 h-5 text-gray-600" />
                </a>
              </div>
            </div>
            
            {/* Links Card */}
            {content.website && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-3">Official Links</h3>
                <div className="space-y-2">
                  <a href={content.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:underline text-sm">
                    <ExternalLink className="w-4 h-4" /> Website
                  </a>
                  {content.whitepaper && (
                    <a href={content.whitepaper} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:underline text-sm">
                      <ExternalLink className="w-4 h-4" /> Whitepaper
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Right Column - Chart & Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Chart Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">{crypto.name} Price Chart</h2>
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  {timeRanges.map(range => (
                    <button
                      key={range.value}
                      onClick={() => setTimeRange(range.value)}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        timeRange === range.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                {chartLoading && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                    <div className="animate-spin w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
                <PriceChart data={chartData} isPositive={isPositive} />
              </div>
            </div>
            
            {/* About Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xl font-bold text-gray-900">About {crypto.name}</h2>
                {content.tagline && <span className="text-sm text-gray-500 hidden md:inline">— {content.tagline}</span>}
              </div>
              
              <div className="space-y-0">
                <ContentSection title={`What is ${crypto.name}?`} content={content.about} defaultOpen={true} />
                <ContentSection title={`Who Created ${crypto.name}?`} content={content.creator} />
                <ContentSection title={`How Does ${crypto.name} Work?`} content={content.howItWorks} />
                <ContentSection title="Security" content={content.security} />
                <ContentSection title={`How to Store ${crypto.symbol}`} content={content.storage} />
                <ContentSection title="Price & Volatility" content={content.volatility} />
                <ContentSection title={`What Makes ${crypto.name} Valuable?`} content={content.value} />
              </div>
            </div>
            
            {/* Trading Info */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Trading Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-500 mb-1">24h Trading Volume</p>
                  <p className="text-xl font-bold text-gray-900">{formatLargeNumber(crypto.volume_24h)}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-500 mb-1">Market Cap Rank</p>
                  <p className="text-xl font-bold text-gray-900">#{crypto.rank}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-500 mb-1">7 Day Change</p>
                  <p className={`text-xl font-bold ${crypto.change_7d >= 0 ? 'text-[#3B82F6]' : 'text-red-500'}`}>
                    {crypto.change_7d >= 0 ? '+' : ''}{crypto.change_7d?.toFixed(2)}%
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-500 mb-1">1 Hour Change</p>
                  <p className={`text-xl font-bold ${crypto.change_1h >= 0 ? 'text-[#3B82F6]' : 'text-red-500'}`}>
                    {crypto.change_1h >= 0 ? '+' : ''}{crypto.change_1h?.toFixed(2)}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <section className="py-8 sm:py-12 px-4 bg-gradient-to-r from-[#001435] to-[#003080]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4">
            Ready to Trade {crypto.name}?
          </h2>
          <p className="text-blue-200/70 text-sm sm:text-base mb-5 sm:mb-6">
            Visit our New Westminster office for competitive rates and instant settlement.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold px-5 py-2.5 sm:px-6 sm:py-3 rounded-lg transition-all text-sm sm:text-base"
            >
              Contact Us <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
            <a 
              href={`tel:${PHONE_NUMBER}`} 
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-[#001435] font-semibold px-5 py-2.5 sm:px-6 sm:py-3 rounded-lg transition-all text-sm sm:text-base"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" /> +1 (778) 759-0400
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
